import React, { useState } from 'react';
import { Button, Card, Space, Table, TablePaginationConfig, Popover, Select, Modal, Input } from 'antd';
import { DeleteOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Rule, RulesWithDeviceType } from '@services/types';

import { selectSearchFilter } from '@services/utils';
import api from '@services/api';
import { toast } from 'react-toastify';
import { DeleteAllRulesRequest } from '@openapi';

const { Column } = Table;
const { Option } = Select;
const mode = ['Rules assigned to an empty group', 'Rules not assigned to a group'];

const UnusedRule: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<any>([]);
  const [selectedMode, setSelectedMode] = useState<string>('Rules assigned to an empty group');
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState<RulesWithDeviceType[]>([]);
  const [openModal, setOpenModal] = useState<RulesWithDeviceType | any>();
  const [additionalData, setAdditionalData] = useState('');
  const showDeleteConfirm = async (rule: DeleteAllRulesRequest['upgradeRuleIds'], text: string) => {
    try {
      if (text === 'permanently delete') {
        await api.rules.deleteRuleList({ upgradeRuleIds: rule });
        setRules(rules.filter((el) => !rule?.includes(el.id)));
        setSelectedRowKeys([]);
        setAdditionalData('');
        setOpenModal(undefined);
        toast.success('Rules was deleted successfully');
      } else {
        toast.error('Error in text input field');
      }
    } catch (error) {
      toast.error('Rules was not deleted');
    }
  };
  React.useEffect(() => {
    if (selectedMode === 'Rules assigned to an empty group') {
      api.rules.getEmptyRules().then((data) => {
        setRules(data.elements);
        setLoading(false);
      });
    } else if (selectedMode === 'Rules not assigned to a group') {
      api.rules.getUnusedRules().then((data) => {
        setRules(data.elements);
        setLoading(false);
      });
    } else return;
  }, [selectedMode]);

  const onSelectChange = (newSelectedRowKeys: any) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };
  return (
    <>
      <Card
        title={`Rule  List`}
        extra={
          <Space>
            <Select
              allowClear
              optionFilterProp="deviceTypes"
              placeholder="Select a Rule Mode"
              onChange={(e: string) => {
                setSelectedMode(e);
              }}
              value={selectedMode}
              showSearch
              filterOption={selectSearchFilter}
            >
              {mode?.map((item) => (
                <Option key={item} value={item}>
                  {item}
                </Option>
              ))}
            </Select>
            <Button
              size="middle"
              danger
              disabled={selectedRowKeys.length < 1}
              type="primary"
              onClick={() => {
                setOpenModal(selectedRowKeys);
              }}
            >
              Delete
            </Button>
          </Space>
        }
        bodyStyle={{ padding: 0 }}
      ></Card>
      <Table
        loading={loading}
        rowSelection={rowSelection}
        dataSource={rules}
        pagination={false}
        rowKey={(record: RulesWithDeviceType) => record.id}
        scroll={{ x: 1200 }}
      >
        <Column
          align="center"
          title="Device Type"
          dataIndex="deviceTypeName"
          render={(text, record: RulesWithDeviceType) => record?.etc?.deviceTypeName}
        />
        <Column
          align="center"
          title="Name"
          dataIndex="name"
          key="Name"
          render={(text, record: RulesWithDeviceType) => record?.name}
        />
        <Column
          align="center"
          title="Ui Version"
          dataIndex="etc.uiVersion"
          render={(text, record: RulesWithDeviceType) => record?.etc?.uiVersion}
        />
        <Column
          align="center"
          title="FW Version"
          dataIndex="etc.firmwareVersion"
          render={(text, record: RulesWithDeviceType) => record.etc?.firmwareVersion}
        />
        <Column
          align="center"
          title="Loader Version"
          dataIndex="etc.loaderVersion"
          render={(text, record: RulesWithDeviceType) => record.etc?.loaderVersion}
        />
        <Column
          align="center"
          title="Scope"
          dataIndex="superGroupName"
          ellipsis
          render={(text, record: RulesWithDeviceType) => record.etc?.superGroupName}
        />
        <Column
          align="center"
          title="CFW1"
          dataIndex="etc.customFirmware1Version"
          render={(text, record: RulesWithDeviceType) => record?.etc?.customFirmware1Version}
        />

        <Column
          align="center"
          title="CFW2"
          dataIndex="etc.customFirmware2Version"
          render={(text, record: RulesWithDeviceType) => record.etc?.customFirmware2Version}
        />
        <Column
          align="center"
          title="CFW3"
          dataIndex="etc.customFirmware3Version"
          render={(text, record: RulesWithDeviceType) => record.etc?.customFirmware3Version}
        />
        <Column
          align="center"
          width="100px"
          fixed="right"
          title="Action"
          key="action"
          render={(text, newRules: RulesWithDeviceType) => (
            <Space size="middle">
              <DeleteOutlined onClick={() => setOpenModal([newRules.id])} />
            </Space>
          )}
        />
      </Table>
      <Modal
        title={'Are you sure delete this Rules'}
        destroyOnClose={true}
        okText={'Delete'}
        okType={'danger'}
        cancelText={'Cancel'}
        onCancel={() => {
          setAdditionalData('');
          setOpenModal(undefined);
        }}
        visible={!!openModal}
        onOk={() => showDeleteConfirm(openModal, additionalData)}
      >
        <div>
          <p>
            To confirm deletion, type <span style={{ fontStyle: 'italic' }}>permanently delete</span> in the text input
            field:
          </p>
          <Input
            placeholder={'permanently delete'}
            onChange={(e) => {
              setAdditionalData(e.target.value);
            }}
          />
        </div>
      </Modal>
    </>
  );
};
export default React.memo(UnusedRule);
